// leadsApi — reads marketing-site leads from the-collective-synergy-api.
//   Discovery calls → /project-briefs (rich, with attachments)
//   Waitlist + General → /contact      (category WAITLIST / INQUIRY / …)
import { SynergyFetch } from '../../../shared/utils/synergyFetch';

export type ProjectBriefStatus = 'NEW' | 'REVIEWED' | 'IN_DISCUSSION' | 'CLOSED';
export type ContactStatus = 'NEW' | 'IN_PROGRESS' | 'CLOSED' | 'SPAM';
export type ContactCategory =
  | 'COMPLAINT'
  | 'INQUIRY'
  | 'BUSINESS_PROPOSAL'
  | 'ORDER_FOR_SERVICE'
  | 'WAITLIST';

export interface ProjectBriefSummary {
  id: string;
  fullName: string;
  email: string;
  serviceNeeded: string;
  status: ProjectBriefStatus;
  attachmentCount: number;
  attachmentsTotalBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectBriefAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string;
}

export interface ProjectBriefDetail extends Omit<ProjectBriefSummary, 'attachmentCount' | 'attachmentsTotalBytes'> {
  productOfInterest?: string | null;
  brand?: string | null;
  industry?: string | null;
  instagram?: string | null;
  otherLinks?: string | null;
  brief?: string | null;
  referenceLink?: string | null;
  attachments: ProjectBriefAttachment[];
}

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  category: ContactCategory;
  message: string;
  brand?: string | null;
  industry?: string | null;
  productOfInterest?: string | null;
  status: ContactStatus;
  createdAt: string;
  updatedAt: string;
}

interface CursorPage<T> {
  data: T[];
  nextCursor?: string;
}

const qs = (params: Record<string, string | number | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length ? '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString() : '';
};

export const leadsApi = {
  // Discovery-call briefs
  listBriefs: (params: { status?: ProjectBriefStatus; serviceNeeded?: string; limit?: number; cursor?: string } = {}) =>
    SynergyFetch.get<CursorPage<ProjectBriefSummary>>(`/project-briefs${qs(params)}`),

  getBrief: (id: string) => SynergyFetch.get<ProjectBriefDetail>(`/project-briefs/${id}`),

  updateBriefStatus: (id: string, status: ProjectBriefStatus) =>
    SynergyFetch.patch<ProjectBriefDetail>(`/project-briefs/${id}`, { status }),

  // Contact submissions (waitlist + general enquiries)
  listContacts: (params: { status?: ContactStatus; category?: ContactCategory; limit?: number; cursor?: string } = {}) =>
    SynergyFetch.get<CursorPage<ContactSubmission>>(`/contact${qs(params)}`),

  updateContactStatus: (id: string, status: ContactStatus) =>
    SynergyFetch.patch<ContactSubmission>(`/contact/${id}/status`, { status }),
};
